import { EDITABLE_TRAILING_ROWS } from '../layout/webDashboardConstants';
import {
	getCanvasBottomBufferPx,
	getElementViewportHeight,
} from '../layout/webDashboardGeometry';

export const createGeometryDraftHandlers = ({
	formatGeometryDraftValue,
	geometryBottomValue,
	geometryContainerHeight,
	geometryContainerWidth,
	geometryHeightValue,
	geometryLeftValue,
	geometryOriginLeft,
	geometryOriginTop,
	geometryRightValue,
	geometryTopValue,
	geometryWidthValue,
	roundGeometryValue,
	setActiveGeometryField,
	setEdit,
	setGeometryDrafts,
}) => ({
	handleGeometryDraftChange: (field, nextValue) => {
		setActiveGeometryField(field);
		setGeometryDrafts((prev) => ({ ...prev, [field]: nextValue }));

		const parsed = Number(nextValue);
		if (!Number.isFinite(parsed)) return;

		if (field === 'width') {
			setEdit((prev) => ({
				...prev,
				width: roundGeometryValue(Math.max(0.0001, parsed)),
			}));
			return;
		}

		if (field === 'height') {
			setEdit((prev) => ({
				...prev,
				height: roundGeometryValue(Math.max(0.0001, parsed)),
			}));
			return;
		}

		if (field === 'top') {
			setEdit((prev) => ({
				...prev,
				top: roundGeometryValue(geometryOriginTop + Math.max(0, parsed)),
			}));
			return;
		}

		if (field === 'left') {
			setEdit((prev) => ({
				...prev,
				left: roundGeometryValue(geometryOriginLeft + Math.max(0, parsed)),
			}));
			return;
		}

		if (field === 'right') {
			const nextRight = Math.max(0, parsed);
			const nextLeft = Math.max(0, geometryContainerWidth - geometryWidthValue - nextRight);
			setEdit((prev) => ({
				...prev,
				left: roundGeometryValue(geometryOriginLeft + nextLeft),
			}));
			return;
		}

		if (field === 'bottom') {
			const nextBottom = Math.max(0, parsed);
			const nextTop = Math.max(0, geometryContainerHeight - geometryHeightValue - nextBottom);
			setEdit((prev) => ({
				...prev,
				top: roundGeometryValue(geometryOriginTop + nextTop),
			}));
		}
	},
	handleGeometryDraftBlur: (field) => {
		setActiveGeometryField((prev) => (prev === field ? null : prev));
		setGeometryDrafts((prev) => ({
			...prev,
			[field]:
				field === 'width' ? formatGeometryDraftValue(geometryWidthValue)
					: field === 'height' ? formatGeometryDraftValue(geometryHeightValue)
						: field === 'top' ? formatGeometryDraftValue(geometryTopValue)
							: field === 'left' ? formatGeometryDraftValue(geometryLeftValue)
								: field === 'right' ? formatGeometryDraftValue(geometryRightValue)
									: formatGeometryDraftValue(geometryBottomValue),
		}));
	},
});

export const runFixedWidthToggle = ({
	dashboard,
	defaultDivHeight,
	defaultDivWidth,
	defaultHeight,
	defaultWidth,
	fixedWidthResizeAlert,
	fixedViewWidth,
	getEditableMetrics,
	getWidgetRectPx,
	getWidgetsCanvasBoundsPx,
	isPercentageLayout,
	lastWidgetsBoxWidth,
	nextChecked,
	setAlertMessage,
	setAlertType,
	setDashboard,
	setFixedViewWidth,
	setIsFixedWidthMode,
	setLastWidgetsBoxWidth,
	setShowAlert,
}) => {
	if (!nextChecked) {
		setIsFixedWidthMode(false);
		setDashboard({
			...dashboard,
			fixedWidth: false,
			widgets: dashboard?.widgets || [],
		});
		return;
	}

	const mainContainer = document.getElementById('web-dashboard-main-container');
	const dashboardElement = document.getElementById('diverted');
	const editable = document.getElementById('editable');
	const widgetsBox = document.querySelector('.widgets-box');

	if (!mainContainer || !dashboardElement || !editable || !widgetsBox) {
		console.warn('DOM elements not ready for fixed width calculation, falling back to simple toggle.');
		setIsFixedWidthMode(true);
		setDashboard({ ...dashboard, fixedWidth: true, widgets: dashboard?.widgets || [] });
		return;
	}

	const style = document.defaultView.getComputedStyle(dashboardElement);
	const gap = parseInt(style.gap, 10) || 0;

	const editableStyle = document.defaultView.getComputedStyle(editable);
	const paddingLeft = parseInt(editableStyle.paddingLeft, 10) || 0;
	const paddingRight = parseInt(editableStyle.paddingRight, 10) || 0;
	const paddingTop = parseInt(editableStyle.paddingTop, 10) || 0;
	const paddingBottom = parseInt(editableStyle.paddingBottom, 10) || 0;
	const totalHorizontalPadding = paddingLeft + paddingRight;
	const totalVerticalPadding = paddingTop + paddingBottom;

	const containerGap = 8;

	const widgetsBoxWidth = widgetsBox.offsetWidth + containerGap;
	if (widgetsBoxWidth !== lastWidgetsBoxWidth) setLastWidgetsBoxWidth(widgetsBoxWidth);

	const mainContainerWidth = mainContainer.offsetWidth;
	if (mainContainerWidth - containerGap !== fixedViewWidth) setFixedViewWidth(mainContainerWidth - containerGap);

	const availableWidthForDiverted = Math.max(1, mainContainerWidth - widgetsBoxWidth);
	const availableHeightForDiverted = getElementViewportHeight(mainContainer);
	const gridRowUnit = Math.max(1, defaultDivHeight + gap);
	const bottomBufferPx = getCanvasBottomBufferPx({
		viewportHeight: availableHeightForDiverted,
		defaultHeight,
		rowUnit: gridRowUnit,
	});
	const currentWidgets = dashboard?.widgets || [];
	const widgetMetrics = getEditableMetrics();
	const { maxBottom: maxWidgetBottom } = getWidgetsCanvasBoundsPx(currentWidgets, widgetMetrics);

	let currentEditableWidthPxForFixed;
	let currentEditableHeightPxForFixed;

	if (isPercentageLayout) {
		currentEditableWidthPxForFixed = Math.max(1, availableWidthForDiverted);
		currentEditableHeightPxForFixed = Math.max(
			defaultHeight + totalVerticalPadding,
			maxWidgetBottom + totalVerticalPadding + gap + bottomBufferPx,
		);
	} else {
		const minGridColsForDefaultWidget = Math.ceil(defaultWidth / (defaultDivWidth + gap));
		const minGridRowsForDefaultWidget = Math.ceil(defaultHeight / (defaultDivHeight + gap));

		let calculatedColumnsForFixed = Math.max(
			1,
			minGridColsForDefaultWidget,
			Math.floor((availableWidthForDiverted - totalHorizontalPadding) / (defaultDivWidth + gap))
		);
		calculatedColumnsForFixed = Math.max(1, calculatedColumnsForFixed);

		currentEditableWidthPxForFixed =
			calculatedColumnsForFixed * (defaultDivWidth + gap) + totalHorizontalPadding;
		currentEditableWidthPxForFixed = Math.max(
			currentEditableWidthPxForFixed,
			defaultWidth + totalHorizontalPadding
		);

		let calculatedRowsForFixed = Math.max(
			minGridRowsForDefaultWidget,
			Math.ceil((maxWidgetBottom + gap) / gridRowUnit) + EDITABLE_TRAILING_ROWS
		);
		calculatedRowsForFixed = Math.max(1, calculatedRowsForFixed);

		currentEditableHeightPxForFixed =
			calculatedRowsForFixed * gridRowUnit + totalVerticalPadding;
		currentEditableHeightPxForFixed = Math.max(
			currentEditableHeightPxForFixed,
			defaultHeight + totalVerticalPadding,
		);
	}

	const fixedCanvasTargetWidth = Math.max(currentEditableWidthPxForFixed, availableWidthForDiverted);
	const maxRight = isPercentageLayout ? currentEditableWidthPxForFixed : fixedCanvasTargetWidth;
	const maxBottom = currentEditableHeightPxForFixed;

	const anyOutside = currentWidgets.some((w) => {
		const rect = getWidgetRectPx(w, widgetMetrics);
		const left = rect.left;
		const top = rect.top;
		const width = rect.width;
		const height = rect.height;

		if (left < 0 || top < 0) return true;
		if (left + width > maxRight) return true;
		if (top + height > maxBottom) return true;
		return false;
	});

	if (anyOutside) {
		setAlertMessage(fixedWidthResizeAlert);
		setAlertType('warning');
		setShowAlert(true);
		return;
	}

	setIsFixedWidthMode(true);
	setDashboard({
		...dashboard,
		fixedWidth: true,
		widgets: dashboard?.widgets || [],
	});
};
